import React, { useCallback, useEffect, useRef, useState } from 'react';
import jsQR from 'jsqr';

/**
 * A QR scanner that lives inside the app.
 *
 * Opening the phone's camera app and tapping a link worked, but it is three
 * steps and two of them happen outside Tychi Desk — people arriving at the
 * door want to open the app and be done. This is the camera, the decoding and
 * the punch in one screen.
 *
 * Two decoders. Chrome on Android has BarcodeDetector built in and it is far
 * cheaper than decoding in JavaScript, so it is used where it exists. iOS
 * Safari does not have it, so jsQR reads the frames there. Trying the native
 * one first means most phones never run the fallback at all.
 *
 * The camera is stopped on every exit path. A scanner that leaves the light on
 * because the user swiped away is the kind of thing people uninstall over, and
 * here they cannot uninstall it — they just stop trusting it.
 */

const SCAN_INTERVAL_MS = 180;

const QrScanner = ({ onResult, onCancel, title = 'Scan the office code' }) => {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const loopRef = useRef(null);
  const detectorRef = useRef(null);
  const doneRef = useRef(false);

  const [error, setError] = useState('');
  const [starting, setStarting] = useState(true);

  const stop = useCallback(() => {
    if (loopRef.current) { clearInterval(loopRef.current); loopRef.current = null; }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  }, []);

  const finish = useCallback((text) => {
    if (doneRef.current) return;
    doneRef.current = true;
    stop();
    onResult(text);
  }, [onResult, stop]);

  const cancel = useCallback(() => {
    doneRef.current = true;
    stop();
    onCancel();
  }, [onCancel, stop]);

  useEffect(() => {
    let cancelled = false;

    const start = async () => {
      try {
        /*
         * facingMode 'environment' asks for the rear camera. A phone that only
         * has a front camera still works — the constraint is a preference, not
         * a requirement, so the browser falls back rather than failing.
         */
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' }, width: { ideal: 1280 } },
          audio: false,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }

        streamRef.current = stream;
        const video = videoRef.current;
        if (!video) return;
        video.srcObject = stream;
        // playsInline matters on iOS: without it Safari takes the video full
        // screen and the scanner disappears behind its own player.
        video.setAttribute('playsinline', 'true');
        await video.play().catch(() => {});
        setStarting(false);

        if (typeof window.BarcodeDetector === 'function') {
          try {
            detectorRef.current = new window.BarcodeDetector({ formats: ['qr_code'] });
          } catch { detectorRef.current = null; }
        }

        loopRef.current = setInterval(async () => {
          const v = videoRef.current;
          if (!v || v.readyState !== 4 || doneRef.current) return;

          if (detectorRef.current) {
            try {
              const found = await detectorRef.current.detect(v);
              if (found?.length && found[0].rawValue) { finish(found[0].rawValue); return; }
            } catch {
              // Detector gave up on this phone; fall through to jsQR for good.
              detectorRef.current = null;
            }
          }

          const canvas = canvasRef.current;
          if (!canvas) return;
          const w = v.videoWidth;
          const h = v.videoHeight;
          if (!w || !h) return;
          canvas.width = w; canvas.height = h;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(v, 0, 0, w, h);
          const img = ctx.getImageData(0, 0, w, h);
          // 'attemptBoth' reads codes shown on a screen, which can be inverted
          // relative to print — and a screen is exactly where ours lives.
          const code = jsQR(img.data, w, h, { inversionAttempts: 'attemptBoth' });
          if (code?.data) finish(code.data);
        }, SCAN_INTERVAL_MS);
      } catch (e) {
        if (cancelled) return;
        setStarting(false);
        setError(
          e?.name === 'NotAllowedError'
            ? 'Camera permission is off. Turn it on for this site in your browser settings, then try again.'
            : 'Could not open the camera on this device.'
        );
      }
    };

    start();
    return () => { cancelled = true; stop(); };
  }, [finish, stop]);

  return (
    <div className="qrs-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <div className="qrs-sheet">
        <div className="qrs-head">
          <h2 className="qrs-title">{title}</h2>
          <button type="button" className="qrs-close" onClick={cancel} aria-label="Close scanner">
            ✕
          </button>
        </div>

        {error ? (
          <div className="qrs-error">
            <p>{error}</p>
            <button type="button" className="btn btn-ghost" onClick={cancel}>Close</button>
          </div>
        ) : (
          <>
            <div className="qrs-stage">
              {/* A live camera feed, not media with speech — nothing to caption. */}
              <video ref={videoRef} className="qrs-video" muted playsInline />
              <div className="qrs-reticle" aria-hidden="true" />
              {starting && <div className="qrs-starting">Opening the camera…</div>}
            </div>
            <p className="qrs-hint">
              Hold your phone so the code on the screen fits inside the box.
            </p>
          </>
        )}

        <canvas ref={canvasRef} className="qrs-canvas" aria-hidden="true" />
      </div>
    </div>
  );
};

export default QrScanner;
